import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Loader2Icon } from 'lucide-react';
import { AdminSidebar } from '../components/admin/sidebar';
import { getUser, clearToken, clearRefreshToken, isAuthenticated } from '../lib/auth';
import type { UserPayload } from '../lib/auth';

export function OrgAdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      if (!isAuthenticated()) {
        navigate('/admin/login');
        return;
      }

      const userData = getUser();
      if (!userData) {
        clearToken();
        clearRefreshToken();
        navigate('/admin/login');
        return;
      }

      if (userData.role !== 'organization_admin') {
        toast.error('Access denied. Organization admin only.');
        navigate('/admin/dashboard');
        return;
      }

      setUser(userData);
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    clearToken();
    clearRefreshToken();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2Icon className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full bg-muted/40">
      <AdminSidebar role={user?.role || null} onLogout={handleLogout} />
      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="flex h-14 items-center gap-4 border-b bg-background px-6">
          <div className="ml-auto flex items-center gap-4">
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium">{user?.username}</span>
              <span className="text-xs text-muted-foreground capitalize">
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
